import {
  prepareDataForApolloCache,
  QueryInfo,
} from '../prepareDataForApolloCache';
import {
  MutationEnablePluginArgs,
  MutationStartBoardExportArgs,
  MutationUpdateBoardOrgArgs,
  MutationUpdateBoardVisibilityArgs,
  MutationUpdateBoardCardCoversPrefArgs,
  MutationUpdateBoardsOrgArgs,
} from '../generated';
import { token } from '@trello/session-cookie';
import { clientVersion } from '@trello/config';
import {
  NetworkError,
  parseNetworkError,
} from '@trello/graphql-error-handling';
import { JSONArray } from '../types';

export const enablePlugin = async (
  obj: object,
  args: MutationEnablePluginArgs,
  context: object,
  info: QueryInfo,
) => {
  const response = await fetch(`/1/boards/${args.boardId}/boardPlugins`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
      'X-Trello-TraceId': args.traceId,
    },
    body: JSON.stringify({
      idPlugin: args.pluginId,
      token,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  return prepareDataForApolloCache({ success: true }, info);
};

export const updateBoardOrg = async (
  obj: object,
  args: MutationUpdateBoardOrgArgs,
  context: object,
  info: QueryInfo,
) => {
  const response = await fetch(`/1/boards/${args.boardId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      keepBillableGuests: args.keepBillableGuests,
      idOrganization: args.orgId,
      token,
    }),
  });

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, info);
};

export const updateBoardsOrg = async (
  obj: object,
  args: MutationUpdateBoardsOrgArgs,
  context: object,
  info: QueryInfo,
) => {
  const networkErrors: NetworkError[] = [];
  const responses: JSONArray = [];

  await Promise.all(
    args.boardIds.map(async (idBoard) => {
      try {
        const response = await fetch(`/1/boards/${idBoard}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Trello-Client-Version': clientVersion,
          },
          credentials: 'include',
          body: JSON.stringify({
            keepBillableGuests: args.keepBillableGuests,
            idOrganization: args.orgId,
            token,
          }),
        });

        if (response.ok) {
          const body = await response.json();
          responses.push(body);
        } else {
          networkErrors.push(await parseNetworkError(response));
        }
      } catch (err) {
        // If we fail the above network errors lets log a unknown one
        networkErrors.push(
          new NetworkError(err.message, { code: 'UNKNOWN_ERROR', status: 500 }),
        );
      }
    }),
  );

  if (networkErrors.length) {
    throw networkErrors;
  }

  return prepareDataForApolloCache(responses, info);
};

export const updateBoardVisibility = async (
  obj: object,
  args: MutationUpdateBoardVisibilityArgs,
  context: object,
  info: QueryInfo,
) => {
  const response = await fetch(`/1/boards/${args.boardId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      keepBillableGuests: args.keepBillableGuests,
      'prefs/permissionLevel': args.visibility,
      idOrganization: args.orgId,
      token,
    }),
  });

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, info);
};

export const updateBoardCardCoversPref = async (
  obj: object,
  args: MutationUpdateBoardCardCoversPrefArgs,
  context: object,
  info: QueryInfo,
) => {
  const response = await fetch(`/1/boards/${args.boardId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      'prefs/cardCovers': args.cardCovers,
      token,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, info);
};

export const boardExportResolver = async (
  board: {
    id: string; // idBoard
  },
  args: {
    id: string; // idExport
  },
  context: object,
  info: QueryInfo,
) => {
  let model = null;

  const apiUrl = `/1/boards/${board.id}/exports/${args.id}`;

  try {
    const response = await fetch(apiUrl, {
      credentials: 'include',
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, info, 'Board') : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const startBoardExport = async (
  obj: object,
  args: MutationStartBoardExportArgs,
  context: object,
  info: QueryInfo,
) => {
  const response = await fetch(`/1/boards/${args.id}/exports`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      token,
      attachments: true,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, info);
};

export const statsResolver = async (
  obj: object,
  args: { id: string },
  context: object,
  info: QueryInfo,
) => {
  const response = await fetch(`/1/boards/${args.id}/stats`, {
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    } else {
      const error = await response.text();
      console.error(error);
      throw new Error(error);
    }
  }

  const model = await response.json();
  return prepareDataForApolloCache(model, info, 'Board');
};

export const boardDashboardViewTileResolver = async (
  board: {
    id: string; // idBoard
  },
  args: {
    id: string; // idTile
  },
  context: object,
  info: QueryInfo,
) => {
  let model = null;

  const apiUrl = `/1/boards/${board.id}/dashboardViewTiles/${args.id}`;

  try {
    const response = await fetch(apiUrl, {
      credentials: 'include',
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, info, 'Board') : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};
